/**
 * Billing Reporting Dashboard Component (Phase 2)
 * System Admin dashboard showing revenue analytics and collection metrics
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Badge, Alert, Spinner, Form } from 'react-bootstrap';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  getRevenueReport,
  getAgingAnalysis,
  getTopPayingSchools,
  getBottomPayingSchools,
} from '../../services/billingPhase2Service';
import schoolsService from '../../services/schoolsService';
import { ChartBar, Money, CalendarBlank, Trophy, WarningCircle, TrendUp } from '@phosphor-icons/react';
import './ReportingDashboard.css';

export const ReportingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [aging, setAging] = useState(null);
  const [topSchools, setTopSchools] = useState([]);
  const [bottomSchools, setBottomSchools] = useState([]);
  const [schools, setSchools] = useState([]);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    (async () => {
      try {
        const data = await schoolsService.listSchools();
        setSchools(Array.isArray(data) ? data : (data.schools || data.data || []));
      } catch (err) {
        // Non-fatal — the school filter just falls back to "All Schools"
      }
    })();
  }, []);

  const loadReportData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [reportData, agingData, topData, bottomData] = await Promise.all([
        getRevenueReport(filter === 'ALL' ? null : filter),
        getAgingAnalysis(filter === 'ALL' ? null : filter),
        getTopPayingSchools(10),
        getBottomPayingSchools(10),
      ]);

      setReport(reportData.report);
      setAging(agingData);
      setTopSchools(topData);
      setBottomSchools(bottomData);
    } catch (err) {
      setError(err.error || 'Failed to load reporting data');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Reports</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  // Prepare data for charts
  const agingData = aging?.aging_buckets ? [
    { name: 'Current', value: aging.aging_buckets.current.amount },
    { name: '1-30 Days', value: aging.aging_buckets['1_30_days'].amount },
    { name: '31-60 Days', value: aging.aging_buckets['31_60_days'].amount },
    { name: '61+ Days', value: aging.aging_buckets['61_plus_days'].amount },
  ] : [];

  const collectionData = report ? [
    { name: 'Collected', value: report.total_revenue_collected },
    { name: 'Outstanding', value: report.total_revenue_expected - report.total_revenue_collected },
  ] : [];

  const COLORS = ['#28a745', '#ffc107', '#fd7e14', '#dc3545'];

  return (
    <Container fluid className="reporting-dashboard mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="section-title"><ChartBar size={20} className="me-2" /> Billing Analytics Dashboard</h2>
          <p className="text-muted">System-wide revenue, collection, and aging analysis</p>
        </Col>
        <Col md="auto">
          <Form.Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ minWidth: '220px' }}
          >
            <option value="ALL">All Schools</option>
            {schools.map((school) => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Key Metrics Summary */}
      {report && (
        <Row className="mb-4">
          <Col md={3} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <h6 className="text-muted mb-2">Total Subscriptions</h6>
                <h3 className="mb-0">{report.total_subscriptions}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <h6 className="text-muted mb-2">Collection Rate</h6>
                <h3 className="mb-0">
                  <Badge bg={report.collection_rate >= 80 ? 'success' : 'warning'}>
                    {report.collection_rate}%
                  </Badge>
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <h6 className="text-muted mb-2">Total Expected</h6>
                <h3 className="mb-0 text-primary">
                  GHS {report.total_revenue_expected.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <h6 className="text-muted mb-2">Late Fees Charged</h6>
                <h3 className="mb-0 text-danger">
                  GHS {report.late_fees_total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Charts Section */}
      <Row className="mb-4">
        {/* Collection Chart */}
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0"><Money size={18} className="me-2" /> Revenue Collection Status</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={collectionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) =>
                      `${name}: GHS ${value.toFixed(0)} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `GHS ${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                  />
                </PieChart>
              </ResponsiveContainer>
              {report && (
                <div className="mt-3 text-center">
                  <small className="text-muted">
                    Collected: <strong>GHS {report.total_revenue_collected.toFixed(2)}</strong> /
                    Expected: <strong>GHS {report.total_revenue_expected.toFixed(2)}</strong>
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Aging Analysis Chart */}
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0"><CalendarBlank size={18} className="me-2" /> Aging Analysis (Days Overdue)</h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={agingData}
                  margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    label={{ value: 'Amount (GHS)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip
                    formatter={(value) =>
                      `GHS ${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                    }
                  />
                  <Bar dataKey="value" fill="#ff7c00" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top and Bottom Paying Schools */}
      <Row className="mb-4">
        {/* Top Paying Schools */}
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0"><Trophy size={18} className="me-2" /> Top 10 Paying Schools (Highest Collection Rate)</h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover size="sm">
                  <thead>
                    <tr>
                      <th>School ID</th>
                      <th className="text-end">Collection Rate</th>
                      <th className="text-end">Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSchools.length > 0 ? (
                      topSchools.map((school, idx) => (
                        <tr key={idx}>
                          <td>
                            <small className="font-monospace">{school.school_id}</small>
                          </td>
                          <td className="text-end">
                            <Badge bg="success">{school.collection_rate}%</Badge>
                          </td>
                          <td className="text-end">
                            <strong>
                              GHS {school.collected.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Bottom Paying Schools (Priority Follow-up) */}
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header className="bg-danger text-white">
              <h5 className="mb-0"><WarningCircle size={18} className="me-2" /> Bottom 10 Schools (Needing Follow-up)</h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover size="sm">
                  <thead>
                    <tr>
                      <th>School ID</th>
                      <th className="text-end">Collection Rate</th>
                      <th className="text-end">Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bottomSchools.length > 0 ? (
                      bottomSchools.map((school, idx) => (
                        <tr key={idx}>
                          <td>
                            <small className="font-monospace">{school.school_id}</small>
                          </td>
                          <td className="text-end">
                            <Badge
                              bg={school.collection_rate < 50 ? 'danger' : 'warning'}
                            >
                              {school.collection_rate}%
                            </Badge>
                          </td>
                          <td className="text-end">
                            <strong className="text-danger">
                              GHS {school.overdue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detailed Summary */}
      {report && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0"><TrendUp size={18} className="me-2" /> Detailed Summary</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <div className="summary-item">
                      <h6 className="text-muted">Revenue Summary</h6>
                      <ul className="list-unstyled">
                        <li>Expected: GHS {report.total_revenue_expected.toFixed(2)}</li>
                        <li>Collected: GHS {report.total_revenue_collected.toFixed(2)}</li>
                        <li>Outstanding: GHS {(report.total_revenue_expected - report.total_revenue_collected).toFixed(2)}</li>
                      </ul>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="summary-item">
                      <h6 className="text-muted">Subscription Status</h6>
                      <ul className="list-unstyled">
                        <li>Current: {report.current_subscriptions}</li>
                        <li>Overdue: {report.overdue_subscriptions}</li>
                        <li>Total: {report.total_subscriptions}</li>
                      </ul>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="summary-item">
                      <h6 className="text-muted">Charges & Discounts</h6>
                      <ul className="list-unstyled">
                        <li>Late Fees: GHS {report.late_fees_total.toFixed(2)}</li>
                        <li>Discounts Given: GHS {report.total_discounts_given.toFixed(2)}</li>
                        <li>Discount Rate: {report.discount_rate}%</li>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ReportingDashboard;
